import React, { useEffect, useState } from 'react'
import {
  Button,
  CloseIcon,
  DialogContent,
  IconButton,
  TextField,
} from '@resiliatech/birdsong'
import { useAppState } from 'Context/AppContext/AppContext'
import { useMutation } from '@apollo/client'
import { GOAL_UPDATES } from 'Apollo/Queries/GoalUpdates'
import { NEEDS_PROGRESS_UPDATE } from 'Apollo/Queries/NeedsProgressUpdate'
import { useSnackbar } from 'Context/SnackbarContext/SnackbarContext'
import UPSERT_GOAL_UPDATE from 'Apollo/Mutations/UpsertGoalUpdate'
import {
  HeaderContainer,
  TitleSection,
  ButtonContainer,
  Popover,
} from './SnapshotDialog.styled'
import { MessagePopover } from '../MessagePopover/MessagePopover'
import {
  dispatchProgressUpdateAdded,
  dispatchProgressUpdateDismissed,
  dispatchProgressUpdateEdited,
} from '../../analytics.utils'
import { GOALS } from '../../../../../../Apollo/Queries/Goals'

type ProgressUpdateResult = {
  success: boolean
  error?: string
}

type SnapshotDialogProps = {
  goalId: number
  goalUpdateId?: number
  goalUpdateDescription?: string
  open: boolean
  taskId?: number
  onUpdateComplete?: (result: ProgressUpdateResult) => void
  onClose: () => void
  showToast?: boolean
  standalone?: boolean
}

export const SnapshotDialog = ({
  goalId,
  goalUpdateId,
  goalUpdateDescription,
  open,
  taskId,
  onUpdateComplete,
  onClose,
  showToast,
  standalone = false,
}: SnapshotDialogProps) => {
  // Feature flag removed - always use new goals list
  const [inputValue, setInputValue] = useState<string>(
    goalUpdateDescription || ''
  )
  const [isDialogVisible, setIsDialogVisible] = useState(true)
  const [messagePopoverState, setMessagePopoverState] = useState<{
    open: boolean
    type: 'confirm' | null
  }>({
    open: false,
    type: null,
  })
  const { popToast } = useSnackbar()

  const { userConfig, nonprofitOrgConfig } = useAppState()
  const { id: userId } = userConfig
  const { orgId: nonprofitOrgId, platformPlan } = nonprofitOrgConfig

  const hasValidInput = inputValue.trim().length > 0

  useEffect(() => {
    setInputValue(goalUpdateDescription || '')
  }, [goalUpdateDescription])

  const handleClose = () => {
    setInputValue('')
    setIsDialogVisible(true)
    setMessagePopoverState({ open: false, type: null })
    onClose()
    dispatchProgressUpdateDismissed({
      goalId,
      userId,
      platformPlan,
      description: inputValue.trim(),
    })
  }

  const handleDoNotUpdate = () => {
    if (hasValidInput) {
      setIsDialogVisible(false)
      setMessagePopoverState({ open: true, type: 'confirm' })
    } else {
      handleClose()
    }
  }

  const handleBackToUpdate = () => {
    setIsDialogVisible(true)
    setMessagePopoverState({ open: false, type: null })
  }

  const [upsertGoalUpdate, { loading }] = useMutation(UPSERT_GOAL_UPDATE, {
    onCompleted: () => {
      if (onUpdateComplete) {
        onUpdateComplete({ success: true })
      }
      if (showToast) {
        popToast({
          content: `Your update has been successfully added.`,
        })
      }
      if (standalone) {
        handleClose()
      }
    },
    onError: (error) => {
      if (onUpdateComplete) {
        onUpdateComplete({ success: false, error: error.message })
      }
      if (showToast) {
        popToast({
          content: `Something went wrong.`,
        })
      }
      if (standalone) {
        handleClose()
      }
    },
    refetchQueries: [
      { query: GOAL_UPDATES, variables: { goalId, nonprofitOrgId } },
      {
        query: NEEDS_PROGRESS_UPDATE,
      },
      {
        query: GOALS,
        variables: {
          activeOnly: true,
          includeOnboarding: true,
          nonprofitOrgId,
        },
      },
      {
        query: GOALS,
        variables: {
          activeOnly: true,
          goalId: goalId.toString(),
          nonprofitOrgId,
        },
      },
    ],
  })

  const handleSubmit = async () => {
    if (!hasValidInput) return

    const goalUpdateInfo = {
      goalId,
      goalUpdateId,
      taskId,
      description: inputValue.trim(),
      reportedAt: new Date().toISOString(),
    }

    await upsertGoalUpdate({
      variables: {
        goalUpdateInfo,
        nonprofitOrgId,
      },
    })

    // Dispatch user event added/edited
    const action = goalUpdateDescription
      ? dispatchProgressUpdateEdited
      : dispatchProgressUpdateAdded

    action({
      progressUpdateId: goalUpdateId,
      goalId,
      userId,
      platformPlan,
      description: inputValue.trim(),
    })
    handleClose()

    popToast({
      content: `Your update has been successfully added.`,
    })
  }

  return (
    <>
      <Popover
        open={open && isDialogVisible}
        onClose={handleClose}
        BackdropProps={{
          invisible: false,
          style: { backgroundColor: 'rgba(26, 26, 26, 0.5)' },
        }}
      >
        <div style={{ padding: '24px' }}>
          <HeaderContainer>
            <TitleSection>
              {goalUpdateDescription ? 'Edit Post' : 'Post Update'}
            </TitleSection>
            <IconButton onClick={handleClose} size='small'>
              <CloseIcon />
            </IconButton>
          </HeaderContainer>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              label='Got any fresh updates on this goal? Let your team know!'
              placeholder='Share your progress, challenges, or next steps...'
              margin='normal'
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                },
              }}
            />
            <ButtonContainer>
              <Button
                variant='primary'
                label='Add Snapshot'
                isDisabled={!hasValidInput || loading}
                onClick={handleSubmit}
              />
            </ButtonContainer>
          </DialogContent>
        </div>
      </Popover>

      {messagePopoverState.open && messagePopoverState.type === 'confirm' && (
        <MessagePopover
          open
          onClose={handleBackToUpdate}
          alertColor='error'
          title='Are you sure?'
          alertMessage='You cannot update this activity later'
          content='These updates help you and your team summarize your goal progress and ensure you have a holistic picture of the effort you and your team perform daily.'
          primaryButton={{
            text: 'Do not update',
            onClick: handleClose,
            variant: 'error',
          }}
          secondaryButton={{
            text: 'Back to update',
            onClick: handleBackToUpdate,
          }}
          showConfirmButtons
        />
      )}
    </>
  )
}
